import { useCallback, useEffect, useState } from 'react';

import {
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Input,
  Row,
  Select,
  Switch,
  Typography,
} from 'antd';
import { ErrorMessage, getIn, useFormikContext } from 'formik';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

import {
  MODULATION_DYNAMIC,
  MODULATION_STATIC,
  SECURITY_A,
  fixedPowerLimitOptions,
  fixedPowerRows,
  smartChargingModulationOptions,
} from './fixedPower';
import FixedPowerTable from './FixedPowerTable';
import * as S from './styled';

import { getMeterDevice } from '../../../hooks/smartcharging';
import { COLORS } from '../../../utils/theme/colors';
import DatePickerCustom from '../../atoms/DatePickerCustom';
import { MeterDevice } from '../../molecules/MeterDevice/MeterDevice';
import GetMeterDeviceModal from '../GetMeterDeviceModal';
import {
  SimChargeBox,
  chargeBoxesWithoutPhase,
} from '../SmartChargingSimulator/scenario';
import { simulatorUrl } from '../SmartChargingSimulator/simulatorUrl';
import { useSmartChargingStations } from '../SmartChargingSimulator/useSmartChargingStations';
import { isIntelbrasTenant } from '../../../utils/isIntelbrasTenant/isIntelbrasTenant';

export type SmartChargingFormProps = {
  loading?: boolean;
  isCreateSmartCharging?: boolean;
  hasTitle?: boolean;
  /** Estações do grupo ainda sem smart charging (criação), para o aviso de fase e o simulador */
  chargeBoxIds?: string[];
  /** Estações carregadas, para quem valida a potência fixa fora do formulário (criação, MV2-2005) */
  onChargeBoxesChange?: (chargeBoxes: SimChargeBox[]) => void;
};

export const smartChargingAlgorithmOptions = [
  { value: 'EQUALIZE', label: 'Geral', id: 1 },
  { value: 'PHASE', label: 'Por Fases', id: 2 },
];

const SmartChargingForm = ({
  loading = false,
  isCreateSmartCharging = false,
  hasTitle,
  chargeBoxIds,
  onChargeBoxesChange,
}: SmartChargingFormProps) => {
  const { values, setFieldValue, errors, touched, submitCount } =
    useFormikContext<any>();
  // Na criação o campo nunca é "tocado" (não está nos valores iniciais): o erro também vale depois de tentar salvar
  const fixedPowerError = getIn(errors, 'smart_charging.connector_powers');
  const showFixedPowerError =
    typeof fixedPowerError === 'string' &&
    (submitCount > 0 || !!getIn(touched, 'smart_charging.connector_powers'));
  const { state } = useLocation<any>();
  const history = useHistory();
  const groupChargeBoxIds: string[] | undefined =
    chargeBoxIds ||
    values.charge_boxs?.map((chargeBox: any) => chargeBox.charge_box_id);
  const { chargeBoxes } = useSmartChargingStations({
    smartChargingId: values.smart_charging?.smart_charging_id,
    chargeBoxIds: groupChargeBoxIds,
  });
  const withoutPhase = chargeBoxesWithoutPhase(chargeBoxes);
  useEffect(() => {
    onChargeBoxesChange?.(chargeBoxes);
  }, [chargeBoxes, onChargeBoxesChange]);
  const modulation = values.smart_charging?.modulation || MODULATION_DYNAMIC;
  const isStatic = !isCreateSmartCharging && modulation === MODULATION_STATIC;
  const nominalA = Number(values.smart_charging?.current_nominal_a) || 0;
  // Ex.: "34 A (limite de 40 A menos a reserva de 6 A)"
  const fixedLimitText = nominalA
    ? `${
        nominalA - SECURITY_A
      } A (limite de ${nominalA} A menos a reserva de ${SECURITY_A} A)`
    : 'o limite do grupo menos a reserva de 6 A';

  // O simulador parte do grupo (ou das estações escolhidas) com o limite do formulário
  const openSimulator = () =>
    history.push(
      simulatorUrl({
        smartChargingId: values.smart_charging?.smart_charging_id,
        chargeBoxIds: groupChargeBoxIds,
        currentNominalA: Number(values.smart_charging?.current_nominal_a),
      }),
    );
  const [enableSmartCharging, setEnableSmartCharging] = useState(
    state?.payload?.smart_charging?.enabled || values.smart_charging.enabled,
  );
  const [linkMeterDeviceModalVisible, setLinkMeterDeviceModalVisible] =
    useState(false);
  const [meterDevice, setMeterDevice] = useState<any>(null);
  const [loadingGetMeterDevice, setLoadingGetMeterDevice] = useState(false);
  const getMeterDeviceResponse = useCallback(async () => {
    if (isCreateSmartCharging) {
      setMeterDevice(values.smart_charging.meter_device);

      return;
    }

    if (values.smart_charging.meter_device_remote_id) {
      try {
        setLoadingGetMeterDevice(true);
        const response = await getMeterDevice({
          deviceRemoteId: values.smart_charging.meter_device_remote_id,
        });

        setMeterDevice({
          device_remote_id: response.data.device_remote_id,
          phaseMaxValue: response?.data?.current?.phase
            ? Math.ceil(
                Math.max(
                  ...Object.values(
                    response?.data?.current?.phase as { [key: string]: number },
                  ),
                ),
              ).toFixed(1)
            : 0,
          timestamp: response.data.timestamp,
          current: response.data.current,
          status: response.data.meter.communication_status,
        });

        setFieldValue(
          'smart_charging.meter_device.mac_address',
          response.data.mac_address,
        );

        return;
      } catch {
        alert('Erro ao buscar medidor');
      } finally {
        setLoadingGetMeterDevice(false);
      }
    }
  }, [
    isCreateSmartCharging,
    setFieldValue,
    values.smart_charging.meter_device,
    values.smart_charging.meter_device_remote_id,
  ]);

  const responseMeterDevice = useCallback(() => {
    return (
      <MeterDevice
        loadingGetMeterDevice={loadingGetMeterDevice}
        meterDevice={meterDevice}
      />
    );
  }, [loadingGetMeterDevice, meterDevice]);

  useEffect(() => {
    getMeterDeviceResponse();
  }, [getMeterDeviceResponse]);

  return (
    <>
      <GetMeterDeviceModal
        setMeterDevice={setMeterDevice}
        linkMeterDeviceModalVisible={linkMeterDeviceModalVisible}
        setLinkMeterDeviceModalVisible={setLinkMeterDeviceModalVisible}
        smartChargingId={values.smart_charging.smart_charging_id}
      />

      {hasTitle && (
        <Row gutter={24} style={{ marginTop: 24 }}>
          <Col md={24}>
            <Typography.Title level={5}>Smart Charging</Typography.Title>
          </Col>
        </Row>
      )}

      <Row gutter={24} style={{ marginTop: 8 }}>
        <Col md={24}>
          <S.EnableSection isCreateSmartCharging={isCreateSmartCharging}>
            {!isCreateSmartCharging && (
              <S.FlexCenter>
                <S.SwitchWrapper>
                  <Switch
                    checked={values.smart_charging.enabled}
                    onChange={(value) => {
                      setEnableSmartCharging(value);

                      setFieldValue('smart_charging.enabled', value);
                    }}
                    disabled={loading}
                  />
                </S.SwitchWrapper>
                <S.Label style={{ marginLeft: 16 }}>
                  Habilitar Smart Charging
                </S.Label>
              </S.FlexCenter>
            )}
            {enableSmartCharging && (
              <>
                <Row>
                  <Col md={24}>
                    <S.Legend>
                      A configuração de Smart Charging vai funcionar apenas para
                      os carregadores que tem suporte.
                    </S.Legend>
                  </Col>
                </Row>
                {isIntelbrasTenant() && (
                  <Row>
                    <Col md={24}>
                      <S.LegendAlert>
                        {`Para sua segurança, consulte um técnico ou instalador
                        qualificado em dimensionamento de Smart Charging ou
                        entre em contato com nosso suporte pelo telefone (48)
                        2106 0006 para obter ajuda.`}
                      </S.LegendAlert>
                    </Col>
                  </Row>
                )}
                <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                  <Col md={12} xs={16} sm={24} lg={6}>
                    <S.Label>Data de início</S.Label>
                    <DatePickerCustom
                      format='DD/MM/YYYY'
                      showTime={{
                        hideDisabledOptions: true,
                        defaultValue: moment().startOf('day'),
                      }}
                      dropdownClassName='dateTimePicker'
                      size='large'
                      name='smart_charging.start_date'
                      placeholder=''
                      onChange={(date: any) => {
                        setFieldValue(
                          'smart_charging.start_date',
                          moment(date).format('YYYY-MM-DD'),
                        );
                      }}
                      required
                      allowClear={false}
                      disabled={loading}
                      maxLength={20}
                      style={{ width: '100%' }}
                      value={
                        values.smart_charging.start_date
                          ? moment(values.smart_charging.start_date)
                          : null
                      }
                    />
                    <ErrorMessage
                      name='smart_charging.start_date'
                      component={S.MessageBox}
                    />
                  </Col>

                  <Col md={12} xs={16} sm={24} lg={6}>
                    <S.Label>Data de término</S.Label>
                    <DatePickerCustom
                      format='DD/MM/YYYY'
                      showTime={{
                        hideDisabledOptions: true,
                        defaultValue: moment().endOf('day'),
                      }}
                      size='large'
                      name='smart_charging.end_date'
                      placeholder=''
                      onChange={(date: any) => {
                        setFieldValue(
                          'smart_charging.end_date',
                          moment(date).format('YYYY-MM-DD'),
                        );
                      }}
                      required
                      allowClear={false}
                      disabled={loading}
                      maxLength={20}
                      value={
                        values.smart_charging.end_date
                          ? moment(values.smart_charging.end_date)
                          : null
                      }
                      style={{ width: '100%' }}
                    />
                    <ErrorMessage
                      name='smart_charging.end_date'
                      component={S.MessageBox}
                    />
                  </Col>

                  <Col md={12} xs={16} sm={24} lg={8}>
                    <S.Label>Corrente Disponível</S.Label>
                    <Input
                      name='smart_charging.current_nominal_a'
                      size='large'
                      disabled={loading}
                      onChange={(e) => {
                        const { value: inputValue } = e.target;
                        const reg = /^-?\d*(\.\d*)?$/;
                        if (
                          reg.test(inputValue) ||
                          inputValue === '' ||
                          inputValue === '-'
                        ) {
                          let maskValue = e.target.value.replace(/\./g, '');
                          if (maskValue.length > 3) {
                            maskValue =
                              maskValue.slice(0, 3) + '.' + maskValue.slice(3);
                          }
                          setFieldValue(
                            'smart_charging.current_nominal_a',
                            maskValue,
                          );
                        }
                      }}
                      maxLength={6}
                      value={values.smart_charging.current_nominal_a}
                      suffix={<InfoCircleOutlined />}
                    />
                    <ErrorMessage
                      name='smart_charging.current_nominal_a'
                      component={S.MessageBox}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  {!isCreateSmartCharging && (
                    <Col md={12} xs={16} sm={24} lg={6}>
                      <S.Label>Modo de distribuição</S.Label>
                      <Select
                        data-testid='smart-charging-modulation'
                        style={{ marginTop: 0, width: '100%', fontWeight: 300 }}
                        size='large'
                        disabled={loading}
                        options={smartChargingModulationOptions}
                        onChange={(value) => {
                          setFieldValue('smart_charging.modulation', value);
                        }}
                        value={modulation}
                      />
                    </Col>
                  )}
                  <Col md={12} xs={16} sm={24} lg={6}>
                    {/* No modo fixo o algoritmo não distribui: só diz se a soma é conferida no total ou em cada fase */}
                    <S.Label data-testid='smart-charging-algorithm-label'>
                      {isStatic
                        ? 'Limite vale para'
                        : 'Distribuição de corrente'}
                    </S.Label>
                    <Select
                      data-testid='smart-charging-algorithm'
                      placeholder='Escolha o Algoritmo'
                      style={{ marginTop: 0, width: '100%', fontWeight: 300 }}
                      size='large'
                      options={
                        isStatic
                          ? fixedPowerLimitOptions
                          : smartChargingAlgorithmOptions
                      }
                      onChange={(value) => {
                        setFieldValue('smart_charging.algorithm', value);
                      }}
                      value={values.smart_charging.algorithm}
                    />

                    <ErrorMessage
                      name='smart_charging.algorithm'
                      component={S.MessageBox}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col md={24}>
                    <S.AlgorithmHelp data-testid='smart-charging-algorithm-help'>
                      {isStatic ? (
                        <>
                          <div>
                            <strong>Potência fixa:</strong> cada conector recebe
                            sempre o valor abaixo, independente de quantos estão
                            carregando. O medidor não é usado.
                          </div>
                          <div>
                            {values.smart_charging.algorithm === 'PHASE' ? (
                              <>
                                <strong>Cada fase:</strong> a soma dos
                                conectores de cada fase cabe em{' '}
                              </>
                            ) : (
                              <>
                                <strong>Soma total:</strong> a soma de todos os
                                conectores cabe em{' '}
                              </>
                            )}
                            {fixedLimitText}
                            {values.smart_charging.algorithm === 'PHASE' &&
                              '. Depende da fase/rotação de cada estação cadastrada corretamente'}
                            .
                          </div>
                        </>
                      ) : (
                        <>
                          {!isCreateSmartCharging && (
                            <div>
                              <strong>Dinâmico:</strong> o limite é dividido
                              entre os conectores que estão carregando.
                            </div>
                          )}
                          <div>
                            <strong>Geral:</strong> o limite vale para a soma de
                            todos os conectores, em qualquer fase. Um grupo de
                            30A divide 30A entre todos os carros.
                          </div>
                          <div>
                            <strong>Por Fases:</strong> o limite vale para cada
                            fase. Um grupo de 30A pode ter até 30A em cada uma
                            das três fases. Depende da fase/rotação de cada
                            estação cadastrada corretamente.
                          </div>
                          <Button
                            type='link'
                            icon={<ExperimentOutlined />}
                            style={{ padding: 0 }}
                            data-testid='smart-charging-open-simulator'
                            onClick={openSimulator}
                          >
                            Simular os dois algoritmos
                          </Button>
                        </>
                      )}
                    </S.AlgorithmHelp>
                    {values.smart_charging.algorithm === 'PHASE' &&
                      withoutPhase.length > 0 && (
                        <Alert
                          data-testid='smart-charging-phase-warning'
                          style={{ marginTop: 12 }}
                          type='warning'
                          showIcon
                          message={`Estação sem fase cadastrada: ${withoutPhase
                            .map((chargeBox) => chargeBox.code)
                            .join(', ')}`}
                          description='No Por Fases o limite é controlado em cada fase. Sem a fase/rotação, a estação é contada nas três fases, mesmo com carro monofásico, e o ganho do Por Fases some. Cadastre a fase no cadastro da estação (Sistema e Fase).'
                        />
                      )}
                  </Col>
                  {isStatic && (
                    <Col md={24}>
                      <FixedPowerTable
                        rows={fixedPowerRows(chargeBoxes)}
                        powers={values.smart_charging.connector_powers}
                        algorithm={values.smart_charging.algorithm}
                        nominalA={Number(
                          values.smart_charging.current_nominal_a,
                        )}
                        disabled={loading}
                        onChange={(powers) =>
                          setFieldValue(
                            'smart_charging.connector_powers',
                            powers,
                          )
                        }
                      />
                      {showFixedPowerError && (
                        <S.MessageBox>{fixedPowerError}</S.MessageBox>
                      )}
                    </Col>
                  )}
                </Row>
                {/* MV2-2005: potência fixa não usa medidor */}
                {!isStatic && (
                  <div data-testid='smart-charging-meter'>
                    <Row gutter={[16, 16]} style={{}}>
                      <Col md={12} xs={16} sm={24} lg={14}>
                        <S.Label>Medidor</S.Label>
                        {meterDevice || loadingGetMeterDevice ? (
                          <Row>
                            {responseMeterDevice()}
                            {!loadingGetMeterDevice && (
                              <>
                                <S.EditButton
                                  disabled={loading}
                                  size='large'
                                  onClick={() =>
                                    setLinkMeterDeviceModalVisible(true)
                                  }
                                >
                                  <EditOutlined />
                                </S.EditButton>
                                <S.DeleteButton
                                  disabled={loading}
                                  onClick={() => {
                                    setMeterDevice(null);
                                    setFieldValue(
                                      'smart_charging.meter_device.mac_address',
                                      '',
                                    );
                                    setFieldValue(
                                      'smart_charging.meter_device_remote_id',
                                      '',
                                    );
                                    setFieldValue(
                                      'smart_charging.meter_device.device_remote_id',
                                      '',
                                    );
                                  }}
                                >
                                  <DeleteOutlined
                                    style={{
                                      color: COLORS.amountStatusNotPaid,
                                    }}
                                  />
                                </S.DeleteButton>
                              </>
                            )}
                          </Row>
                        ) : (
                          <Button
                            size='large'
                            onClick={() => setLinkMeterDeviceModalVisible(true)}
                          >
                            <LinkOutlined />
                            Configurar
                          </Button>
                        )}
                      </Col>
                    </Row>
                    <Checkbox
                      checked={
                        values.smart_charging.meter_device_remote_in_circuit
                      }
                      onChange={(e) => {
                        setFieldValue(
                          'smart_charging.meter_device_remote_in_circuit',
                          e.target.checked,
                        );
                      }}
                    >
                      Medidor mesmo circuito
                    </Checkbox>
                  </div>
                )}
              </>
            )}
          </S.EnableSection>
        </Col>
      </Row>
    </>
  );
};
export default SmartChargingForm;
